import { __ } from '@wordpress/i18n';
import { useSelect } from '@wordpress/data';
import { useState, useEffect } from '@wordpress/element';
import {
	__experimentalGrid as Grid,
} from '@wordpress/components';
import { RangeControl, Spinner, SelectControl, TabPanel, ToggleControl, Panel, PanelBody, __experimentalNumberControl as NumberControl } from '@wordpress/components';
import { useBlockProps, InspectorControls, RichText } from '@wordpress/block-editor';
import './editor.scss';

export default function Edit( { attributes, setAttributes } ) {
	const { postGrid, postPerPage, PostOrder, showHidePagi, showHideRT, showHideCat, showHideLink, PostOrderBy, postType } = attributes;
	const [ options, setOptions ] = useState( [] );

	// Fetch post types
	const fetchPostTypes = async () => {
		try {
			const response = await fetch( '/wp-json/wp/v2/types' );
			const data = await response.json();
			const allowedPostTypes = [ 'post', 'page', 'product' ];
			const filteredPostTypes = Object.values( data ).filter( ( type ) =>
				allowedPostTypes.includes( type.slug )
			);
			const formattedPostTypes = filteredPostTypes.map( ( type ) => ( {
				label: type.name,
				value: type.slug,
			} ) );
			setOptions( formattedPostTypes );
		} catch ( error ) {
			console.error( 'Error fetching post types:', error );
		}
	};

	useEffect( () => {
		fetchPostTypes();
	}, [] );

	// Fetch posts based on selected settings
	const posts = useSelect( ( select ) => {
		return select( 'core' ).getEntityRecords( 'postType', postType, { per_page: postPerPage, _embed: true, order: PostOrder.toLowerCase(), orderby: PostOrderBy } );
	}, [ postGrid, postPerPage, PostOrder, postType, PostOrderBy, showHidePagi, showHideLink, showHideCat, showHideRT ] );

	// Function to calculate the reading time
	const calculateReadTime = ( content ) => {
		const wordsPerMinute = 200; // Average reading speed
		const wordCount = content.split( /\s+/ ).length;
		const readTimeInMinutes = Math.ceil( wordCount / wordsPerMinute );
		return readTimeInMinutes;
	};

	return (
		<section { ...useBlockProps() }>
			<InspectorControls>
				<Panel>
					<TabPanel
						className="my-tab-panel"
						activeClass="active-tab"
						tabs={ [
							{
								name: 'tab2',
								title: __( 'Post Settings', 'advance-post-grid' ),
								className: 'tab-two',
							},
							{
								name: 'tab1',
								title: __( 'Layouts Settings', 'advance-post-grid' ),
								className: 'layouts',
							},
						] }
					>
						{ ( tab ) => (
							<div className={ tab.className }>
								{ tab.name === 'tab1' && (
									<>
										<PanelBody title={ __( 'Post Grid', 'advance-post-grid' ) }>
											<RangeControl
												help={ __( 'Please select how much column you would like this.', 'advance-post-grid' ) }
												initialPosition={ postGrid }
												label={ __( 'Grid', 'advance-post-grid' ) }
												max={ 4 }
												min={ 2 }
												onChange={ ( value ) => setAttributes( { postGrid: value } ) }
											/>
										</PanelBody>
										<PanelBody title={ __( 'Style Options', 'advance-post-grid' ) }>

											<ToggleControl
												label="Pagination"
												help={ __( 'Show / Hide - Pagination' ) }
												checked={ showHidePagi }
												onChange={ ( value ) => setAttributes( { showHidePagi: value } ) }
											/>
											<ToggleControl
												label="Post Link"
												help={ __( 'Show / Hide - Post Link' ) }
												checked={ showHideLink }
												onChange={ ( value ) => setAttributes( { showHideLink: value } ) }
											/>
											<ToggleControl
												label="Categories"
												help={ __( 'Show / Hide - Categories' ) }
												checked={ showHideCat }
												onChange={ ( value ) => setAttributes( { showHideCat: value } ) }
											/>
											<ToggleControl
												label="Read Time"
												help={ __( 'Show / Hide - Read Time' ) }
												checked={ showHideRT }
												onChange={ ( value ) => setAttributes( { showHideRT: value } ) }
											/>
										</PanelBody>
									</>
								) }
								{ tab.name === 'tab2' && (
									<PanelBody title={ __( 'Settings', 'advance-post-grid' ) }>
										<SelectControl
											label="Post Type"
											value={ postType }
											options={ options }
											onChange={ ( value ) => setAttributes( { postType: value } ) }
										/>
										<NumberControl
											label="Post Per Page"
											value={ postPerPage }
											onChange={ ( value ) => setAttributes( { postPerPage: value } ) }
										/>
										<SelectControl
											label="Order"
											value={ PostOrder }
											options={ [
												{ label: 'Ascending', value: 'ASC' },
												{ label: 'Descending', value: 'DESC' },
											] }
											onChange={ ( value ) => setAttributes( { PostOrder: value } ) }
										/>
										<SelectControl
											label="Order By"
											value={ PostOrderBy }
											options={ [
												{ label: 'Title', value: 'title' },
												{ label: 'Date', value: 'date' },
											] }
											onChange={ ( value ) => setAttributes( { PostOrderBy: value } ) }
										/>
									</PanelBody>
								) }
							</div>
						) }
					</TabPanel>
				</Panel>
			</InspectorControls>

			{ posts && posts.length > 0 ? (
				<>
					<Grid columns={ postGrid }>
						{ posts.map( ( post ) => (
							<div key={ post.id } className="wo-advance-post-grid-single">

								<div className="wo-advance-post-grid-image">
									{ post._embedded[ 'wp:featuredmedia' ] && post._embedded[ 'wp:featuredmedia' ][ 0 ] && (
										<img
											className="featured-image"
											src={ post._embedded[ 'wp:featuredmedia' ][ 0 ].source_url }
											alt={ post._embedded[ 'wp:featuredmedia' ][ 0 ].alt_text }
											width="150"
											height="150"
										/>
									) }
								</div>
								<div className="wo-advance-post-grid-content">
									{
										showHideCat ? <div className="wo-advance-post-grid-single-cats">
											{ post._embedded[ 'wp:term' ] &&
										post._embedded[ 'wp:term' ][ 0 ] &&
										post._embedded[ 'wp:term' ][ 0 ].map( ( category ) => (
											<span key={ category.id } className="wo-advance-post-grid-single-cat">
												<span>{ category.name }</span>
											</span>
										) ) }
										</div> : <></>
									}

									<div className="wo-advance-post-grid-heading">
										<h3><RichText.Content value={ post.title.rendered } /></h3>
									</div>
									<div className="wo-advance-post-grid-excerpt">
										<RichText.Content value={ post.excerpt.rendered } />
									</div>
								</div>
								<div className="wo-advance-post-grid-single-bottom">
									{
										showHideLink ? <div className="wo-advance-post-grid-single-button">
											<span className="wo-advance-post-grid-button">Read More</span>
										</div> : <></>
									}

									{
										showHideRT ? <div className="wo-advance-post-grid-single-date">
											<span className="p3">{ new Date( post.date ).toLocaleDateString( 'en-US', {
												month: 'short',
												day: '2-digit',
												year: 'numeric',
											} ) }
											</span>
											<span className="p3">
												{ post.content.rendered ? calculateReadTime( post.content.rendered ) : 0 } min read
											</span>
										</div> : <></>
									}

								</div>
							</div>
						) ) }
					</Grid>
					{
						showHidePagi ? <div className="pagination">
							<span role="button" className="pre-page d-flex disabled" title="previous">
								<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path d="M19 12H6M6 12L11 7M6 12L11 17" stroke="#228476" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
								</svg>
							</span>
							<div className="pagination-numbers">
								<span className="current">1</span>
								<span className="inactive" contentEditable="false" style={ { cursor: 'pointer' } }>2</span>
								<span className="inactive" contentEditable="false" style={ { cursor: 'pointer' } }>3</span>
							</div>
							<span className="next-page d-flex disabled" title="next">
								<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path d="M5 12H18M18 12L13 7M18 12L13 17" stroke="#228476" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
								</svg>
							</span>
						</div> : <></>
					}

				</>

			) : (
				<Spinner
					style={ {
						height: 'calc(4px * 20)',
						width: 'calc(4px * 20)',
					} }
				/>
			) }
		</section>
	);
}
